import React, { useState, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import useModalStore from "../../stores/modalStore";
import { EVENT_TYPE } from "../../domain";
import LoadingSpinner from "../../LoadingSpinner";
import useUserStore from "../../stores/userStore";
import useEventStore from "../../stores/eventStore";
import AddEventModal from "../modals/AddEventModal";
import { addEventWithDefaults } from "../../services/eventService";
import { hasPermission } from "../../utils/permissions";
import {
    LogIn, LogOut as LogOutIcon, AlertCircle, Thermometer, CalendarOff, Gift,
    DollarSign, Scissors, BarChart2, Phone, Briefcase, ShieldCheck, BookOpen, Users,
    CalendarDays
} from "lucide-react";

const ActionButtons = ({ handleLogout }) => {
    const navigate = useNavigate();
    const userIdToFirstName = useUserStore(state => state.userIdToFirstName);
    const isUserAuthenticated = useUserStore(state => state.currentUser?.authenticated === true);
    const selectedLocation = useUserStore(state => state.selectedLocation);
    const { currentUser, setEmployeeEvents } = useUserStore();
    const { selectedEvent, setSelectedEvent } = useEventStore();
    const openModal = useModalStore(state => state.openModal);
    const [loadingType, setLoadingType] = useState(null);
    const [loadingNeedsReview, setLoadingNeedsReview] = useState(false);
    const [showClockInSuccess, setShowClockInSuccess] = useState(false);
    const [showClockOutSuccess, setShowClockOutSuccess] = useState(false);
    const recentEventsRef = useRef({});
    const EVENT_SUBMIT_COOLDOWN_MS = 60000;

    const handleClickWithLoading = async (eventType, wasForgotten = false, notes = "") => {
        const now = Date.now();
        const lastSubmitted = recentEventsRef.current[eventType];

        if (lastSubmitted && now - lastSubmitted < EVENT_SUBMIT_COOLDOWN_MS) {
            console.warn(`Ignoring duplicate ${eventType} submission within cooldown window.`);
            return;
        }

        recentEventsRef.current[eventType] = now;

        setLoadingType(eventType);
        setLoadingNeedsReview(wasForgotten);

        try {
            if (wasForgotten) {
                openModal("addEventModal", {
                    startingEventType: eventType,
                    userId: currentUser?.user_id,
                    userFirstName: userIdToFirstName[currentUser?.user_id],
                    needsReview: true,
                    isForgottenEvent: true //tells the backend to relax some of the sequence ordering requirements
                });
            } else {
                try {
                    const result = await addEventWithDefaults(
                        (newEvent) => {
                            setEmployeeEvents(currentUser.user_id, (prev) => [newEvent, ...prev]);
                        },
                        currentUser.user_id,
                        currentUser.user_id,
                        eventType,
                        selectedLocation,
                        wasForgotten,
                        Date.now(),
                        Date.now(),
                        notes
                    )

                    if (eventType === EVENT_TYPE.CLOCK_IN) {
                        setShowClockInSuccess(result);
                        setTimeout(() => setShowClockInSuccess(false), 15000);
                    }

                    if (eventType === EVENT_TYPE.CLOCK_OUT) {
                        setShowClockOutSuccess(result);
                        setTimeout(() => setShowClockOutSuccess(false), 15000);
                    }
                } catch (error) {
                    console.error(`Failed to handle ${eventType}:`, error);
                }
            }
        } finally {
            setLoadingType(null);
            setLoadingNeedsReview(false);
        }
    };

    if (!isUserAuthenticated) {
        return (
            <div className="flex flex-col items-center justify-center py-12 gap-4">
                <div className="text-lg font-medium text-gray-600">Processing Login. Please wait...</div>
                <LoadingSpinner />
            </div>
        );
    }

    // Shared animated button renderer for clock in/out
    const ClockButton = ({ eventType, label, loading, success, successLabel, icon: Icon, colorClasses }) => (
        <div className="h-[52px] relative flex justify-center items-center w-full">
            <AnimatePresence mode="wait" initial={false}>
                {loading ? (
                    <motion.div
                        key={`${eventType}-spinner`}
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.8 }}
                        transition={{ duration: 0.2 }}
                        className="absolute inset-0 flex justify-center items-center"
                    >
                        <LoadingSpinner />
                    </motion.div>
                ) : success ? (
                    <motion.div
                        key={`${eventType}-success`}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        transition={{ duration: 0.25 }}
                        className="absolute inset-0 flex justify-center items-center text-emerald-600 font-bold text-lg"
                    >
                        {successLabel}
                    </motion.div>
                ) : (
                    <motion.button
                        key={`${eventType}-button`}
                        onClick={() => handleClickWithLoading(eventType)}
                        className={`!absolute !inset-0 !flex !items-center !justify-center !gap-2 !rounded-xl !text-base !font-semibold !border-none !shadow-md hover:!shadow-lg !transition-all ${colorClasses}`}
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.95 }}
                        transition={{ duration: 0.2 }}
                    >
                        <Icon size={20} />
                        {label}
                    </motion.button>
                )}
            </AnimatePresence>
        </div>
    );

    // Quick action tile
    const ActionTile = ({ icon: Icon, label, onClick, className = "" }) => (
        <button
            onClick={onClick}
            className={`!flex !flex-col !items-center !justify-center !gap-1.5 !p-3 !bg-white !border !border-gray-200 !rounded-xl hover:!border-indigo-300 hover:!bg-indigo-50/50 hover:!shadow-md !transition-all !text-gray-700 hover:!text-indigo-700 !min-h-[72px] ${className}`}
        >
            <Icon size={20} />
            <span className="text-xs font-medium leading-tight text-center">{label}</span>
        </button>
    );

    return (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
            {/* ── Clock In/Out Hero Card ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200/60 p-5 flex flex-col gap-3">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-1">Time Clock</h3>

                <ClockButton
                    eventType={EVENT_TYPE.CLOCK_IN}
                    label="Clock In"
                    loading={loadingType === EVENT_TYPE.CLOCK_IN && !loadingNeedsReview}
                    success={showClockInSuccess}
                    successLabel="Clocked In!"
                    icon={LogIn}
                    colorClasses="!bg-emerald-600 hover:!bg-emerald-700 !text-white"
                />

                <ClockButton
                    eventType={EVENT_TYPE.CLOCK_OUT}
                    label="Clock Out"
                    loading={loadingType === EVENT_TYPE.CLOCK_OUT && !loadingNeedsReview}
                    success={showClockOutSuccess}
                    successLabel="Clocked Out!"
                    icon={LogOutIcon}
                    colorClasses="!bg-rose-600 hover:!bg-rose-700 !text-white"
                />

                <div className="flex gap-2 mt-1">
                    <button
                        onClick={() => handleClickWithLoading(EVENT_TYPE.CLOCK_IN, true)}
                        disabled={loadingType === EVENT_TYPE.CLOCK_IN && loadingNeedsReview}
                        className="!flex-1 !flex !items-center !justify-center !gap-1 !py-2 !px-2 !text-xs !font-medium !text-gray-500 !bg-gray-50 !border !border-gray-200 !rounded-lg hover:!bg-amber-50 hover:!text-amber-700 hover:!border-amber-200 !transition-colors"
                    >
                        {loadingType === EVENT_TYPE.CLOCK_IN && loadingNeedsReview ? (
                            <LoadingSpinner />
                        ) : (
                            <><AlertCircle size={13} /> Forgot In</>
                        )}
                    </button>
                    <button
                        onClick={() => handleClickWithLoading(EVENT_TYPE.CLOCK_OUT, true)}
                        disabled={loadingType === EVENT_TYPE.CLOCK_OUT && loadingNeedsReview}
                        className="!flex-1 !flex !items-center !justify-center !gap-1 !py-2 !px-2 !text-xs !font-medium !text-gray-500 !bg-gray-50 !border !border-gray-200 !rounded-lg hover:!bg-amber-50 hover:!text-amber-700 hover:!border-amber-200 !transition-colors"
                    >
                        {loadingType === EVENT_TYPE.CLOCK_OUT && loadingNeedsReview ? (
                            <LoadingSpinner />
                        ) : (
                            <><AlertCircle size={13} /> Forgot Out</>
                        )}
                    </button>
                </div>
            </div>

            {/* ── Quick Actions Grid ── */}
            <div className="lg:col-span-2 bg-white rounded-2xl shadow-sm border border-gray-200/60 p-5">
                <h3 className="text-xs font-bold uppercase tracking-wider text-gray-400 mb-3">Quick Actions</h3>
                <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-2">
                    <ActionTile
                        icon={CalendarDays}
                        label="See Schedule"
                        onClick={() => openModal("seeScheduleModal")}
                        className="!border-indigo-200 !text-indigo-700 hover:!bg-indigo-50 hover:!border-indigo-300"
                    />
                    <ActionTile
                        icon={Thermometer}
                        label="Sick Leave"
                        onClick={() => {
                            setSelectedEvent(prev => ({ ...prev, notes: "", event_timestamp: Date.now() }));
                            openModal("sickModal");
                        }}
                    />
                    <ActionTile
                        icon={CalendarOff}
                        label="Time Off"
                        onClick={() => {
                            setSelectedEvent(prev => ({ ...prev, notes: "", event_timestamp: Date.now() }));
                            openModal("timeOffModal");
                        }}
                    />
                    <ActionTile
                        icon={Gift}
                        label="Comped Service"
                        onClick={() => {
                            setSelectedEvent(prev => ({ ...prev, notes: "", value: 0.0, event_timestamp: Date.now() }));
                            openModal("compedServiceModal");
                        }}
                    />
                    <ActionTile
                        icon={DollarSign}
                        label="Pay Summary"
                        onClick={() => {
                            setSelectedEvent({ ...selectedEvent, user_id: currentUser.user_id });
                            openModal("paySummaryModal", { userId: currentUser.user_id });
                        }}
                    />
                    <ActionTile
                        icon={Scissors}
                        label="Split Tip"
                        onClick={() => openModal("splitTipCalculatorModal")}
                    />
                    <ActionTile
                        icon={ShieldCheck}
                        label="ESST Report"
                        onClick={() => openModal("esstReportModal", { userId: currentUser.user_id })}
                        className="!border-teal-200 !text-teal-700 hover:!bg-teal-50 hover:!border-teal-300"
                    />
                    <ActionTile
                        icon={BookOpen}
                        label="Handbook"
                        onClick={() => openModal("handbookModal")}
                    />

                    {hasPermission('view_earnings') && (
                        <ActionTile
                            icon={BarChart2}
                            label="Salon Earnings"
                            onClick={() => openModal("earningsModal")}
                        />
                    )}

                    {hasPermission('view_business_trends') && (
                        <ActionTile
                            icon={Users}
                            label="Employee Compare"
                            onClick={() => openModal("employeeComparisonModal")}
                            className="!border-indigo-200 !text-indigo-700 hover:!bg-indigo-50 hover:!border-indigo-300"
                        />
                    )}

                    {hasPermission('run_payroll') && (
                        <ActionTile
                            icon={Briefcase}
                            label="Payroll"
                            onClick={() => openModal("runPayrollModal")}
                            className="!border-purple-200 !text-purple-700 hover:!bg-purple-50 hover:!border-purple-300"
                        />
                    )}

                    {hasPermission('view_daysmart') && (
                        <ActionTile
                            icon={Scissors}
                            label="DaySmart"
                            onClick={() => navigate('/daysmart')}
                            className="!border-teal-200 !text-teal-700 hover:!bg-teal-50 hover:!border-teal-300"
                        />
                    )}

                    {hasPermission('view_call_center') && (
                        <ActionTile
                            icon={Phone}
                            label="Communication Center"
                            onClick={() => navigate('/communication-center')}
                            className="!border-blue-200 !text-blue-700 hover:!bg-blue-50 hover:!border-blue-300"
                        />
                    )}
                </div>
            </div>

            <AddEventModal />
        </div>
    );
};

export default ActionButtons;
